import { isAxiosError } from "axios";
import { useCallback, useState } from "react";
import { useGetLicensePlateCountryIdentifier } from "@/lib/api/generated/license-plate/license-plate";

/**
 * HTTP status the backend returns when a plate has no known registration
 * (see `VehicleRegistrationResource`'s `exceptionToStatus` mapping for
 * `LicensePlateNotFoundException`) — distinct from a transient upstream
 * failure, which surfaces as a 5xx.
 */
const NOT_FOUND_STATUS = 412;

export interface LicensePlateLookupResult {
  readonly brand: string | null;
  readonly model: string | null;
  readonly buildYear: number | null;
}

/**
 * On-demand license-plate registration lookup, for confirming a vehicle
 * while creating a dossier (not for populating additional form fields — the
 * create-dossier form only collects the plate itself; the backend does its
 * own enrichment).
 *
 * Deliberately not a plain `useQuery` fired on every keystroke: `lookup()`
 * is meant to be called once the admin has finished typing a plate (e.g. on
 * blur or Enter), matching how `LicensePlateInput`'s `actionSlot` is meant
 * to be wired. Editing the plate again should call `reset()` first so a
 * stale confirmation isn't shown against a since-changed value.
 */
export function useLicensePlateLookup() {
  const [target, setTarget] = useState<{
    country: string;
    identifier: string;
  } | null>(null);

  const query = useGetLicensePlateCountryIdentifier(
    target?.country ?? "",
    target?.identifier ?? "",
    {
      query: {
        // The generated hook defaults `enabled` to true whenever
        // country/identifier are non-null — which an empty string
        // satisfies. Only fetch once `lookup()` has actually set a target.
        enabled: target !== null,
      },
    },
  );

  const lookup = useCallback(
    (country: string, identifier: string) => {
      const isSameTarget =
        target?.country === country && target.identifier === identifier;

      if (isSameTarget) {
        // `LicensePlateInput`'s blur and its lookup button both call
        // `lookup()` for the same click (clicking the button blurs the
        // input first). While the first call is still in flight, treat the
        // second as a genuine no-op rather than firing a duplicate request.
        // Once it has settled, though, a same-plate call is the admin
        // explicitly retrying after a failure — refetch rather than
        // silently doing nothing.
        if (!query.isFetching) {
          void query.refetch();
        }
        return;
      }

      setTarget({ country, identifier });
    },
    [target, query.isFetching, query.refetch],
  );

  const reset = useCallback(() => {
    setTarget(null);
  }, []);

  const notFound =
    query.isError &&
    isAxiosError(query.error) &&
    query.error.response?.status === NOT_FOUND_STATUS;

  const result: LicensePlateLookupResult | undefined = query.data
    ? {
        brand: query.data.brand ?? null,
        model: query.data.model ?? null,
        buildYear: query.data.buildYear ?? null,
      }
    : undefined;

  return {
    lookup,
    reset,
    result,
    isLoading: query.isFetching,
    notFound,
    // A transient failure (anything other than "not found"), so the caller
    // can offer a retry rather than treating it as "unknown plate".
    isError: query.isError && !notFound,
  };
}
